/*  Contact — neutral "Talk to a smile expert" landing.
    ────────────────────────────────────────────────────────────────
    iter100.142 — Rewritten to host the same guided-callback form the
    Guards & Retainer product pages use, with a fifth "Aligners" pill
    (Aligners is Invisique's flagship product). The mega-menu footer
    CTA now deep-links here instead of dumping visitors on the Night
    Guard product page — much more welcoming for a visitor who's
    unsure which product is right for them.

    The old general-message form + `/api/contact` endpoint is retired;
    all Contact submissions now flow into `db.leads` alongside the
    other product enquiries so ops sees them in the Leads Hub.
*/
import React from "react";
import { Phone, Envelope, MapPin, HeadCircuit } from "@phosphor-icons/react";
import GuidedCallbackForm from "@/components/site/GuidedCallbackForm";


// 5-item interest palette — Aligners first because it's Invisique's
// flagship product and the neutral Contact page attracts undecided
// visitors, most of whom are aligner-curious.
const CONTACT_INTEREST_OPTIONS = [
    { slug: "aligners", label: "Aligners" },
    { slug: "night-guard", label: "Night Guard" },
    { slug: "anti-snoring", label: "Anti-Snoring Guard" },
    { slug: "sports-guard", label: "Sports Guard" },
    { slug: "retainer", label: "Retainer" },
];


export default function Contact() {
    return (
        <div className="bg-white" data-testid="contact-page">
            {/* Callback form first — the mega-menu CTA drops visitors here
                who explicitly want to talk to a specialist. Keep the form
                above the fold so no scroll-hunting is required. */}
            <GuidedCallbackForm
                productSlug="aligners"
                defaultInterest="aligners"
                themeTint="indigo"
                testPrefix="contact"
                eyebrow="Request a callback"
                headline="Talk to a smile expert."
                blurb="Tell us a bit about you and what you're looking for. An Invisique specialist will call you back and guide you to the right treatment and a certified provider near you."
                interestOptions={CONTACT_INTEREST_OPTIONS}
            />

            {/* Context section below — headline, coordinates, credibility.
                Retained so visitors who scrolled past the form (or landed
                via direct link) still get brand context + phone/email/HQ
                fallbacks. */}
            <section className="relative overflow-hidden bg-white">
                <div className="absolute inset-0 blue-grid-bg opacity-40 mask-fade-b pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-start">
                    {/* Left — headline + coordinates */}
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white border border-border/70 shadow-sm mb-6" data-testid="contact-hero-pill">
                            <HeadCircuit size={14} weight="duotone" color="hsl(192 100% 31%)" />
                            <span className="text-xs font-semibold text-foreground">
                                Prefer to reach us directly? <span className="text-muted-foreground font-normal">· We&apos;re here from 9am–8pm IST</span>
                            </span>
                        </div>
                        <div className="overline text-primary mb-5" data-testid="contact-hero-eyebrow">
                            The Invisique specialist team
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight" data-testid="contact-hero-title">
                            Real humans. Real answers.
                            <br />
                            <span className="text-primary">One call, all the options.</span>
                        </h2>
                        <p className="mt-6 text-base text-muted-foreground max-w-xl" data-testid="contact-hero-blurb">
                            Aligners, night guards, snoring, sports protection, retainers — one Invisique specialist can walk you through every option and connect you with a certified provider near you, usually within
                            <span className="text-foreground font-semibold"> 15 minutes during business hours</span>.
                        </p>

                        {/* Coordinates strip */}
                        <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-2xl" data-testid="contact-coords">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                                    <Phone size={18} weight="duotone" color="hsl(192 100% 31%)" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Phone</div>
                                    <div className="text-sm font-semibold text-foreground">+91 80 1234 5678</div>
                                    <div className="text-[10.5px] text-muted-foreground">Mon–Sat · 9am–8pm IST</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                                    <Envelope size={18} weight="duotone" color="hsl(192 100% 31%)" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Email</div>
                                    <div className="text-sm font-semibold text-foreground">hello@invisique.in</div>
                                    <div className="text-[10.5px] text-muted-foreground">We reply within 24 hours</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                                    <MapPin size={18} weight="duotone" color="hsl(192 100% 31%)" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">HQ</div>
                                    <div className="text-sm font-semibold text-foreground">Koramangala</div>
                                    <div className="text-[10.5px] text-muted-foreground">Bengaluru, India</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right — credibility card */}
                    <div className="lg:col-span-5 relative hidden lg:block">
                        <div className="absolute -top-6 -right-6 w-64 h-64 rounded-full bg-primary/15 blur-3xl pointer-events-none" aria-hidden="true" />
                        <div className="relative bg-card border border-border rounded-3xl p-8 shadow-lg text-center">
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Why the callback works</div>
                            <div className="font-display text-2xl font-bold mt-2 leading-tight">Free · No commitment · Under 15 min</div>
                            <div className="text-sm text-muted-foreground mt-3">
                                Aligners, night guards, snoring, sport protection, retainers — one call, all the answers.
                            </div>
                            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                                {[
                                    { k: "500+", v: "Certified providers" },
                                    { k: "50+", v: "Cities served" },
                                    { k: "24h", v: "Response promise" },
                                ].map((s) => (
                                    <div key={s.v} className="bg-secondary/40 rounded-xl py-3">
                                        <div className="font-display text-lg font-bold text-primary">{s.k}</div>
                                        <div className="text-[10px] text-muted-foreground leading-tight">{s.v}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
